/**
 * 학원 등록 (데스크탑 와이드 폼) — PC 콘솔에서 운영 학원이 아직 없을 때 표시.
 * 모바일 AcademyApplyPage와 동일한 draft → submit 흐름. 폼은 AcademyForm 공유.
 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import { PrimaryBtn, GhostBtn } from './consoleUi';
import AcademyForm, { emptyForm, formFromAcademy, buildAcademyData, missingRequired } from './AcademyForm';
import DaumPostcodeModal from '../../components/common/DaumPostcodeModal';

const ConsoleRegister = () => {
  const { userData } = useAuth();
  const { reloadAcademies } = useAcademy();
  const [draftId, setDraftId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [postcodeOpen, setPostcodeOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    if (!userData?.uid) return;
    setLoading(true);
    let draft = await academyService.getMyDraftAcademy(userData.uid);
    if (!draft) draft = await academyService.createDraftAcademy(userData.uid);
    setDraftId(draft.id);
    setForm(formFromAcademy(draft));
    setLoading(false);
  }, [userData?.uid]);

  useEffect(() => { load(); }, [load]);

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !draftId) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const url = await academyService.uploadAcademyPromoPhoto(draftId, file);
        uploaded.push({ url, label: '' });
      }
      set('photos', [...form.photos, ...uploaded]);
    } catch (err) {
      alert('사진 업로드에 실패했어요.');
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = (i) => set('photos', form.photos.filter((_, idx) => idx !== i));
  const setPhotoLabel = (i, v) => set('photos', form.photos.map((p, idx) => (idx === i ? { ...p, label: v } : p)));
  const addTuition = () => set('tuition', [...form.tuition, { label: '', price: '' }]);
  const setTuition = (i, k, v) => set('tuition', form.tuition.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const delTuition = (i) => set('tuition', form.tuition.filter((_, idx) => idx !== i));
  const openPostcode = () => setPostcodeOpen(true);

  const missing = missingRequired(form);
  const canSubmit = missing.length === 0;

  const onSubmit = async () => {
    if (!canSubmit || submitting || !draftId) return;
    setSubmitting(true);
    try {
      await academyService.updateDraftAcademy(draftId, buildAcademyData(form));
      await academyService.submitDraftAcademy(draftId, userData.uid);
      await reloadAcademies();
      setDone(true);
    } catch (err) {
      console.error('학원 등록 실패:', err);
      alert('등록에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };
  const onSaveDraft = async () => {
    if (!draftId) return;
    await academyService.updateDraftAcademy(draftId, buildAcademyData(form));
    alert('임시 저장했어요.');
  };

  const formProps = {
    form, set, uploading, photoInputId: 'register-photos',
    onAddPhotos, removePhoto, setPhotoLabel,
    addTuition, setTuition, delTuition, openPostcode,
  };

  if (loading) return <FullScreen data-fullwidth><Loading>불러오는 중…</Loading></FullScreen>;

  if (done) {
    return (
      <FullScreen data-fullwidth>
        <DoneCard>
          <IoCheckmarkCircle size={64} color="#4A7CFE" />
          <h2>학원 등록 완료!</h2>
          <p>1등급 운영자 확인 후 디렉토리에 공개돼요.<br />승인 전에도 콘솔에서 미리 운영 준비를 할 수 있어요.</p>
          <PrimaryBtn onClick={() => window.location.reload()}>콘솔로 들어가기</PrimaryBtn>
        </DoneCard>
      </FullScreen>
    );
  }

  return (
    <FullScreen data-fullwidth>
      <TopStrip><Brand><LogoIcon>1급</LogoIcon> 일등급 학원 콘솔</Brand></TopStrip>
      <Body>
        <Head>
          <h1>학원 등록</h1>
          <p>큰 화면에서 학원 정보를 한 번에 입력하세요. 등록(제출)하면 바로 콘솔이 열려요.</p>
        </Head>
        <Panel><AcademyForm {...formProps} /></Panel>
        <Foot>
          <FootHint>{canSubmit ? '모든 필수 항목이 채워졌어요. 등록할 수 있어요.' : `필수 항목 ${missing.length}개 남음 — ${missing.join(', ')}`}</FootHint>
          <FootBtns>
            <GhostBtn onClick={onSaveDraft}>임시 저장</GhostBtn>
            <PrimaryBtn onClick={onSubmit} disabled={!canSubmit || submitting}>{submitting ? '등록 중…' : '학원 등록하기'}</PrimaryBtn>
          </FootBtns>
        </Foot>
      </Body>
      {postcodeOpen && (
        <DaumPostcodeModal
          onClose={() => setPostcodeOpen(false)}
          onComplete={(addr) => set('location', addr)}
        />
      )}
    </FullScreen>
  );
};

export default ConsoleRegister;

const FullScreen = styled.div` min-height: 100vh; background: #F7F8FA; max-width: none !important; `;
const Loading = styled.div` display: flex; align-items: center; justify-content: center; min-height: 100vh; color: #9CA3AF; `;
const TopStrip = styled.div` background: #fff; border-bottom: 1px solid #EAEDF3; padding: 14px 28px; `;
const Brand = styled.div` display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 800; color: #1A1A2E; `;
const LogoIcon = styled.span`
  width: 30px; height: 30px; border-radius: 8px; background: #4A7CFE; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800;
`;
const Body = styled.div` max-width: 880px; margin: 0 auto; padding: 32px 24px 64px; `;
const Head = styled.div`
  margin-bottom: 20px;
  h1 { font-size: 26px; font-weight: 800; color: #111827; }
  p { font-size: 14px; color: #6B7280; margin-top: 8px; }
`;
const Panel = styled.div` background: #fff; border: 1px solid #EAEDF3; border-radius: 16px; padding: 24px; `;
const Foot = styled.div`
  margin-top: 22px; display: flex; align-items: center; justify-content: space-between; gap: 16px;
  background: #fff; border: 1px solid #EAEDF3; border-radius: 14px; padding: 16px 20px;
`;
const FootHint = styled.div` font-size: 13px; font-weight: 600; color: #6B7280; `;
const FootBtns = styled.div` display: flex; gap: 10px; flex-shrink: 0; `;
const DoneCard = styled.div`
  max-width: 480px; margin: 80px auto; background: #fff; border: 1px solid #EAEDF3; border-radius: 18px;
  padding: 48px 32px; text-align: center;
  h2 { font-size: 24px; font-weight: 800; color: #111827; margin: 18px 0 12px; }
  p { font-size: 14px; line-height: 1.65; color: #6B7280; margin-bottom: 26px; }
`;
